import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { rbacUrl } from './api';

const STORAGE_KEY = 'permissionCodes';
const EVENT = 'hoteldesk-rbac-updated';

const PermissionContext = createContext(null);

function normalizePermissionEntry(entry) {
  if (entry == null) return '';
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry === 'object') {
    return String(
      entry.code ?? entry.Code ?? entry.permissionCode ?? '',
    ).trim();
  }
  return '';
}

function readCodesFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizePermissionEntry).filter(Boolean);
  } catch {
    return [];
  }
}

function normalizePermissionList(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizePermissionEntry).filter(Boolean);
}

// ── A switch that does not exist yet is ON ──────────────────────────────
//
// The UI ships new permission codes (a page-open switch, a new action) ahead
// of - or without - the SQL that seeds them. A user who holds ANY code would
// otherwise fail every check for a code their database has never heard of,
// and lose the whole page. Worst case seen: the User Permissions page itself
// vanished, which is the one screen that could have fixed it.
//
// Two answers, in order:
//   1. knownCodes - the catalogue the server sends with me/permissions. A code
//      not in it does not exist here, so it cannot have been turned off.
//   2. Before the catalogue has arrived (older API, or the first paint after
//      login): a page-open code (PG_* / PAGE_*) is enforced only for a user
//      who holds at least one PG_* code. The seed that creates those grants
//      every one of them to every role, so holding none means the seed has
//      not run on this database.
const KNOWN_KEY = 'rbacKnownCodes';

export function readKnownCodes() {
  try {
    const raw = sessionStorage.getItem(KNOWN_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizePermissionEntry).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function storeKnownCodes(list) {
  try {
    sessionStorage.setItem(KNOWN_KEY, JSON.stringify(list));
  } catch {
    /* private mode */
  }
}

const upper = (x) => String(x || '').trim().toUpperCase();

export function codeIsUnknown(code, known) {
  const k = known || readKnownCodes();
  if (!k.length) return false;
  const c = upper(code);
  return !k.some((x) => upper(x) === c);
}

export function pageGateInactive(code, codes) {
  const c = upper(code);
  if (!c.startsWith('PG_') && !c.startsWith('PAGE_')) return false;
  return !(codes || []).some((x) => upper(x).startsWith('PG_'));
}

// Switches that start OFF for everybody (a feature given to chosen users only
// while it is new). None of the fail-open rules below apply to them - no codes
// stored, a switch the server has not heard of - they are ON only when the
// user actually holds the code. The server hands them out the same way
// (PermissionResolverService.OptInCodes).
const OPT_IN_CODES = new Set(['POS_ONLINE_ORDERS_OPEN']);
export const isOptInCode = (code) => OPT_IN_CODES.has(upper(code));

// The one rule, shared by the hook and by hasStoredPermission.
export function decidePermission(code, codes, known) {
  if (!code) return true;
  if (isOptInCode(code)) return (codes || []).some((x) => upper(x) === upper(code));
  if (!codes || codes.length === 0) return true; // no RBAC set up: allowed
  if (codeIsUnknown(code, known)) return true;
  if (!(known || []).length && pageGateInactive(code, codes)) return true;
  const c = upper(code);
  return codes.some((x) => upper(x) === c);
}

export function notifyRbacUpdated() {
  window.dispatchEvent(new Event(EVENT));
}

export function PermissionProvider({ children }) {
  const [codes, setCodes] = useState(() => readCodesFromStorage());
  const [known, setKnown] = useState(() => readKnownCodes());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const fromStorage = readCodesFromStorage();
    setCodes(fromStorage);

    const token = sessionStorage.getItem('token');
    const userId = sessionStorage.getItem('userId');
    if (!token || !userId) return;

    try {
      setLoading(true);
      // API_BASE_URL already ends in /api (see rbac/api.js). This used to add
      // a second /api, so the request 404'd in silence and the codes saved at
      // login stood for the whole session - a switch turned OFF on User
      // Permissions never reached the screen until the next login.
      const res = await fetch(rbacUrl('rbac/me/permissions'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const list = data.permissionCodes ?? data.PermissionCodes ?? [];
        if (Array.isArray(list)) {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
          setCodes(normalizePermissionList(list));
        }
        const catalogue = data.knownCodes ?? data.KnownCodes;
        if (Array.isArray(catalogue)) {
          const clean = normalizePermissionList(catalogue);
          storeKnownCodes(clean);
          setKnown(clean);
        }
      }
    } catch {
      /* keep sessionStorage copy */
    } finally {
      setLoading(false);
    }
  }, []);

  // Once per app load: the login response carries the user's codes but not
  // the catalogue, and a seed script run after login should reach the screen
  // without a fresh sign-in. Nothing waits on it - the storage copy answers
  // until it lands.
  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const on = () => {
      setCodes(readCodesFromStorage());
      // Login / logout: the provider mounted before there was a token, so
      // the mount-time fetch found nothing to do. Fetch now (the catalogue
      // too). After logout there is no token and this returns at once.
      refresh();
    };
    window.addEventListener(EVENT, on);
    window.addEventListener('storage', on);
    return () => {
      window.removeEventListener(EVENT, on);
      window.removeEventListener('storage', on);
    };
  }, [refresh]);

  const useGranularRbac = codes.length > 0;

  const hasPermission = useCallback(
    (code) => decidePermission(code, codes, known),
    [codes, known],
  );

  const hasAnyPermission = useCallback(
    (list) => {
      if (!list?.length) return true;
      return list.some((c) => hasPermission(c));
    },
    [hasPermission],
  );

  const value = useMemo(
    () => ({
      hasPermission,
      hasAnyPermission,
      permissionCodes: codes,
      useGranularRbac,
      loading,
      refresh,
    }),
    [hasPermission, hasAnyPermission, codes, useGranularRbac, loading, refresh],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

const LEGACY_PERMISSION_FALLBACK = {
  hasPermission: () => true,
  hasAnyPermission: () => true,
  permissionCodes: [],
  useGranularRbac: false,
  loading: false,
  refresh: () => {},
};

export function usePermission() {
  const ctx = useContext(PermissionContext);
  return ctx ?? LEGACY_PERMISSION_FALLBACK;
}

/** @deprecated use usePermission */
export function useRbacPermissions() {
  return usePermission();
}
